import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'

export interface PeriodMetric {
  plan: number
  fact: number
  completion: number
}

export interface PeriodHistoryRecord {
  period_id: number
  period_start: string
  period_end: string
  period_name: string
  weeks: number
  year_number: number
  month_number: number
  metrics: {
    marketing_budget: PeriodMetric
    cost_per_lead: PeriodMetric
    leads: PeriodMetric
    appointments: PeriodMetric
    new_visitors: PeriodMetric
    private_sessions_count: PeriodMetric
    private_sessions_amount: PeriodMetric
    other_income: PeriodMetric
    refunds_amount: PeriodMetric
    gross_revenue: PeriodMetric
    new_sales_count: PeriodMetric
    new_sales_amount: PeriodMetric
    trial_payments: PeriodMetric
    renewals_count: PeriodMetric
    renewals_amount: PeriodMetric
    upgrades_count: PeriodMetric
    upgrades_amount: PeriodMetric
    returning_clients_count: PeriodMetric
    returning_clients_amount: PeriodMetric
    membership_fees_new: PeriodMetric
    membership_fees_subscription_upgrades: PeriodMetric
    membership_fees_subscription_returning: PeriodMetric
    new_subscriptions_count: PeriodMetric
    new_subscriptions_amount: PeriodMetric
    subscription_renewals_count: PeriodMetric
    subscription_renewals_amount: PeriodMetric
    subscription_upgrades_count: PeriodMetric
    subscription_upgrades_amount: PeriodMetric
    subscription_returning_clients_count: PeriodMetric
    subscription_returning_clients_amount: PeriodMetric
  }
}

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
]

function pct(plan: number, fact: number): number {
  return plan > 0 ? (fact / plan) * 100 : 0
}

function sum(rows: any[], field: string): number {
  return rows.reduce((acc, row) => acc + (Number(row[field]) || 0), 0)
}

export function usePlanHistory(studioId: number): { history: PeriodHistoryRecord[]; loading: boolean } {
  const [history, setHistory] = useState<PeriodHistoryRecord[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!studioId) return
    load()

    async function load() {
      setLoading(true)
      try {
        const [periodsResult, weeklyResult] = await Promise.all([
          supabase
            .from('metric_periods')
            .select('period_id, period_start, period_end, period_len_days, year_number, month_number')
            .order('period_start', { ascending: false }) as unknown as Promise<{ data: any[] | null; error: any }>,
          supabase
            .from('studio_weekly_plan_vs_fact')
            .select('*')
            .eq('studio_internal_id', studioId)
            .order('period_id')
            .order('week_number_in_period') as unknown as Promise<{ data: any[] | null; error: any }>,
        ])

        if (periodsResult.error) throw periodsResult.error
        if (weeklyResult.error) throw weeklyResult.error

        const periods = periodsResult.data ?? []
        const weeklyRows = weeklyResult.data ?? []

        if (weeklyRows.length === 0) {
          setHistory([])
          return
        }

        const periodMap = new Map<number, (typeof periods)[number]>(
          periods.map((p) => [p.period_id, p])
        )

        // Group weekly rows by period_id
        const byPeriod = new Map<number, any[]>()
        for (const row of weeklyRows) {
          const id: number = row.period_id
          if (!byPeriod.has(id)) byPeriod.set(id, [])
          byPeriod.get(id)!.push(row)
        }

        const result: PeriodHistoryRecord[] = []

        for (const [periodId, rows] of byPeriod.entries()) {
          const period = periodMap.get(periodId)
          if (!period) continue

          const leadsPlan     = sum(rows, 'weekly_leads_plan')
          const leadsFact     = sum(rows, 'weekly_leads_fact')
          const marketingPlan = sum(rows, 'weekly_marketing_budget_plan')
          const marketingFact = sum(rows, 'weekly_marketing_expenses_amount_fact')

          // Build a standard plan/fact/completion metric from two field names
          const m = (pf: string, ff: string) => {
            const p = sum(rows, pf), f = sum(rows, ff)
            return { plan: p, fact: f, completion: pct(p, f) }
          }

          const metrics: PeriodHistoryRecord['metrics'] = {
            marketing_budget: { plan: marketingPlan, fact: marketingFact, completion: pct(marketingPlan, marketingFact) },
            cost_per_lead: {
              plan: leadsPlan > 0 ? marketingPlan / leadsPlan : 0,
              fact: leadsFact > 0 ? marketingFact / leadsFact : 0,
              completion: 0,
            },
            leads:                              { plan: leadsPlan, fact: leadsFact, completion: pct(leadsPlan, leadsFact) },
            appointments:                       m('weekly_appointments_plan',                              'weekly_appointments_fact'),
            new_visitors:                       m('weekly_new_visitors_plan',                              'weekly_new_visitors_fact'),
            private_sessions_count:             m('weekly_private_sessions_count_plan',                   'weekly_private_sessions_count_fact'),
            private_sessions_amount:            m('weekly_private_sessions_amount_plan',                  'weekly_private_sessions_amount_fact'),
            other_income:                       { plan: 0, fact: sum(rows, 'weekly_other_income_fact'), completion: 0 },
            refunds_amount: (() => {
              const p = sum(rows, 'weekly_refunds_amount_plan'), f = sum(rows, 'weekly_refunds_amount_fact')
              return { plan: p, fact: f, completion: pct(Math.abs(p), Math.abs(f)) }
            })(),
            gross_revenue:                      m('weekly_gross_revenue_plan',                            'weekly_gross_revenue_fact'),
            new_sales_count:                    m('weekly_new_sales_plan',                                'weekly_new_sales_fact'),
            new_sales_amount:                   m('weekly_new_sales_amount_plan',                         'weekly_new_sales_amount_fact'),
            trial_payments:                     { plan: 0, fact: sum(rows, 'weekly_trial_payments_amount_fact'), completion: 0 },
            renewals_count:                     m('weekly_renewals_count_plan',                           'weekly_renewals_count_fact'),
            renewals_amount:                    m('weekly_renewals_amount_plan',                          'weekly_renewals_amount_fact'),
            upgrades_count:                     m('weekly_upgrades_count_plan',                           'weekly_upgrades_count_fact'),
            upgrades_amount:                    m('weekly_upgrades_amount_plan',                          'weekly_upgrades_amount_fact'),
            returning_clients_count:            m('weekly_returning_clients_count_plan',                  'weekly_returning_clients_count_fact'),
            returning_clients_amount:           m('weekly_returning_clients_amount_plan',                 'weekly_returning_clients_amount_fact'),
            membership_fees_new:                m('weekly_membership_fees_new_plan',                      'weekly_membership_fees_new_amount_fact'),
            membership_fees_subscription_upgrades:  m('weekly_membership_fees_subscription_upgrades_plan',   'weekly_membership_fees_subscription_upgrades_amount_fact'),
            membership_fees_subscription_returning: m('weekly_membership_fees_subscription_returning_plan',  'weekly_membership_fees_subscription_returning_amount_fact'),
            new_subscriptions_count:            m('weekly_new_subscriptions_count_plan',                  'weekly_new_subscriptions_count_fact'),
            new_subscriptions_amount:           m('weekly_new_subscriptions_amount_plan',                 'weekly_new_subscriptions_amount_fact'),
            subscription_renewals_count:        m('weekly_subscription_renewals_count_plan',              'weekly_subscription_renewals_count_fact'),
            subscription_renewals_amount:       m('weekly_subscription_renewals_amount_plan',             'weekly_subscription_renewals_amount_fact'),
            subscription_upgrades_count:        m('weekly_subscription_upgrades_count_plan',              'weekly_subscription_upgrades_count_fact'),
            subscription_upgrades_amount:       m('weekly_subscription_upgrades_amount_plan',             'weekly_subscription_upgrades_amount_fact'),
            subscription_returning_clients_count:   m('weekly_subscription_returning_clients_count_plan',    'weekly_subscription_returning_clients_count_fact'),
            subscription_returning_clients_amount:  m('weekly_subscription_returning_clients_amount_plan',   'weekly_subscription_returning_clients_amount_fact'),
          }

          result.push({
            period_id: periodId,
            period_start: period.period_start,
            period_end: period.period_end,
            period_name: `${MONTH_NAMES[period.month_number - 1]} ${period.year_number}`,
            weeks: Math.round(period.period_len_days / 7) || rows.length,
            year_number: period.year_number,
            month_number: period.month_number,
            metrics,
          })
        }

        // Sort descending by period start
        result.sort((a, b) => b.period_start.localeCompare(a.period_start))
        setHistory(result)
      } catch (err) {
        console.error('usePlanHistory error:', err)
        setHistory([])
      } finally {
        setLoading(false)
      }
    }
  }, [studioId])

  return { history, loading }
}
